import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatearFecha, fechaParaInput } from '../utils/fecha';
import { deudaDelCliente } from '../utils/prestamo';
import { buscarClientes, normalizar } from '../utils/buscar';

const moneda = (n) => '$' + Math.round(n || 0).toLocaleString('es-CO');

// Un cliente que ya tiene préstamo vuelve a pedir. Antes había que salir de su
// hoja, ir a Nuevo Préstamo y buscarlo otra vez por el nombre; ahora se añade
// desde donde ya se está mirando. Es la pestaña que no corresponde a ningún
// préstamo guardado todavía.
const NUEVO = 'nuevo';

function ClientList({ clients, onRefresh, settings = {} }) {
  // Repartir la cartera entre socios es una forma de trabajar concreta. Quien
  // compra la herramienta no la usa, y le aparecía un campo «Propietario» que
  // no significaba nada. Se enciende en Configuración.
  const usaPropietarios = settings.usa_propietarios === true;

  const [cedula, setCedula] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [owner, setOwner] = useState('');
  const [address, setAddress] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('todos');
  const [busquedaCliente, setBusquedaCliente] = useState('');
  const [error, setError] = useState('');
  const [loansData, setLoansData] = useState({});
  const [scoresData, setScoresData] = useState({});
  const [publicProfiles, setPublicProfiles] = useState({});
  const [paymentsData, setPaymentsData] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [modalClient, setModalClient] = useState(null);
  // Edición: el cliente que se está editando y los valores del formulario.
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({});
  // Un borrador por préstamo: { idDelPrestamo: {monto, interés, fechas...} }.
  // Se guardan aparte del formulario del cliente para poder ir y venir entre
  // préstamos sin perder lo escrito en los otros.
  const [borradores, setBorradores] = useState({});
  const [prestamoActivo, setPrestamoActivo] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [errorEdit, setErrorEdit] = useState('');

  // Préstamos, pagos y puntajes de todos los clientes en una sola petición.
  //
  // Antes se pedía pieza por pieza dentro de un bucle: los préstamos de cada
  // cliente, el puntaje de cada cliente y los pagos de cada préstamo. Con 114
  // clientes son 342 peticiones y el navegador solo hace 6 a la vez: unos diez
  // segundos mirando una tabla vacía.
  useEffect(() => {
    if (clients.length === 0) return;

    let cancelado = false;
    axios.get('/api/clients/resumen')
      .then(res => {
        if (cancelado) return;
        const prestamos = {};
        const pagos = {};
        const puntajes = {};

        (res.data.clientes || []).forEach(c => {
          prestamos[c.id] = c.prestamos || [];
          puntajes[c.id] = { credit_score: c.credit_score || 0 };
          (c.prestamos || []).forEach(p => { pagos[p.id] = p.pagos || []; });
        });

        setLoansData(prestamos);
        setPaymentsData(pagos);
        setScoresData(puntajes);
      })
      .catch(err => console.error('Error cargando el listado:', err));

    return () => { cancelado = true; };
  }, [clients]);

  // Los propietarios se deducen de los propios datos, no de una lista fija:
  // si mañana entra otro socio, aparece solo al asignarle el primer cliente.
  const SIN_ASIGNAR = 'sin asignar';
  const owners = [...new Set(clients.map(c => c.owner || SIN_ASIGNAR))].sort();
  const contarPor = (o) => clients.filter(c => (c.owner || SIN_ASIGNAR) === o).length;

  const porPropietario = ownerFilter === 'todos'
    ? clients
    : clients.filter(c => (c.owner || SIN_ASIGNAR) === ownerFilter);

  // Buscar por nombre, cédula o teléfono. Con cien clientes en pantalla, dar
  // con uno era bajar rodando la lista; y quien llama por teléfono suele
  // identificarse por la cédula, no por cómo quedó escrito su nombre.
  const termino = normalizar(busquedaCliente).trim();
  const visibleClients = termino === ''
    ? porPropietario
    : buscarClientes(porPropietario, busquedaCliente);

  const handleAddClient = async (e) => {
    e.preventDefault();
    setError('');

    if (!name || !cedula || !phone) {
      setError('❌ Nombre, cédula y teléfono son obligatorios');
      return;
    }

    if (clients.some(c => c.cedula === cedula)) {
      setError('❌ Este cliente ya existe. Cédula duplicada.');
      return;
    }

    try {
      await axios.post('/api/clients', { cedula, name, phone, email, address, owner });
      setCedula('');
      setName('');
      setPhone('');
      setEmail('');
      setAddress('');
      setOwner('');
      setError('');
      onRefresh();
      alert('✅ Cliente creado correctamente');
    } catch (err) {
      if (err.response?.status === 400 && err.response?.data?.error?.includes('UNIQUE')) {
        setError('❌ Este cliente ya existe (cédula duplicada)');
      } else {
        setError('❌ Error: ' + err.message);
      }
    }
  };

  const togglePublicProfile = async (clientId, currentStatus) => {
    try {
      await axios.post(`/api/clients/${clientId}/enable-public-profile`, {
        enabled: !currentStatus,
        nickname: ''
      });
      setPublicProfiles(prev => ({
        ...prev,
        [clientId]: !currentStatus
      }));
    } catch (err) {
      alert('Error actualizando perfil público: ' + err.message);
    }
  };

  // Cuánto debe el cliente en total, sumando todos sus préstamos activos.
  // Antes se miraba solo el primero y se comparaba contra el capital, sin
  // intereses: un cliente con dos préstamos podía darse por saldado teniendo
  // uno vivo, y el programa ofrecía borrarlo.
  const deuda = (client) =>
    deudaDelCliente(loansData[client.id] || [], paymentsData);

  const getMaturityStatus = (loan) => {
    if (!loan || !loan.maturity_date) return { icon: '•', color: '#6b7280', status: 'Sin fecha' };

    const today = new Date();
    const maturityDate = new Date(loan.maturity_date);
    const daysUntil = Math.ceil((maturityDate - today) / (1000 * 60 * 60 * 24));

    if (daysUntil < 0) {
      return { icon: '🔴', color: '#ef4444', status: `Vencido hace ${Math.abs(daysUntil)} días` };
    } else if (daysUntil <= 7) {
      return { icon: '🟡', color: '#f59e0b', status: `Vence en ${daysUntil} días` };
    } else {
      return { icon: '🟢', color: '#10b981', status: `Vence en ${daysUntil} días` };
    }
  };

  // Abre la ficha de edición con los datos actuales del cliente y, si lo tiene,
  // los de su préstamo. Editar el préstamo es la única forma de ponerle fecha
  // de vencimiento a los que se crearon antes de que existiera ese campo.
  const camposDelPrestamo = (p) => ({
    amount: p.amount,
    interest_rate: p.interest_rate,
    frequency: p.frequency || 'monthly',
    start_date: fechaParaInput(p.start_date),
    maturity_date: fechaParaInput(p.maturity_date),
    fiador_nombre: p.fiador_nombre || '',
    fiador_cedula: p.fiador_cedula || '',
    fiador_telefono: p.fiador_telefono || '',
    fiador_direccion: p.fiador_direccion || '',
    fiador_email: p.fiador_email || ''
  });

  // `empezarEnNuevo` abre la hoja directamente en la pestaña de préstamo
  // nuevo, para el botón ➕ de la fila.
  const abrirEdicion = (client, empezarEnNuevo = false) => {
    const prestamos = loansData[client.id] || [];
    setErrorEdit('');
    setEditando({ client, prestamos });

    setForm({
      cedula: client.cedula || '',
      name: client.name || '',
      phone: client.phone || '',
      email: client.email || '',
      address: client.address || '',
      owner: client.owner || ''
    });

    // Se cargan todos de una vez. Cambiar de préstamo en el selector solo
    // cambia cuál se muestra, así que lo escrito en los demás sigue ahí.
    const iniciales = {};
    prestamos.forEach(p => { iniciales[p.id] = camposDelPrestamo(p); });
    // La pestaña de préstamo nuevo empieza con lo de Configuración y la fecha
    // de hoy, igual que Nuevo Préstamo. Solo se crea si se le pone monto.
    iniciales[NUEVO] = {
      amount: '',
      interest_rate: settings.interest_rate ?? 10,
      frequency: settings.frequency || 'monthly',
      start_date: new Date().toISOString().split('T')[0],
      maturity_date: '',
      fiador_nombre: '', fiador_cedula: '', fiador_telefono: '',
      fiador_direccion: '', fiador_email: ''
    };
    setBorradores(iniciales);
    setPrestamoActivo(
      empezarEnNuevo || prestamos.length === 0 ? NUEVO : prestamos[0].id
    );
  };

  const campo = (nombre) => ({
    value: form[nombre] ?? '',
    onChange: (e) => setForm(prev => ({ ...prev, [nombre]: e.target.value }))
  });

  // Campos del préstamo que se esté mostrando
  const campoPrestamo = (nombre) => ({
    value: borradores[prestamoActivo]?.[nombre] ?? '',
    onChange: (e) => setBorradores(prev => ({
      ...prev,
      [prestamoActivo]: { ...prev[prestamoActivo], [nombre]: e.target.value }
    }))
  });

  const guardarEdicion = async (e) => {
    e.preventDefault();
    setErrorEdit('');

    if (!form.name || !form.cedula || !form.phone) {
      setErrorEdit('❌ Nombre, cédula y teléfono son obligatorios');
      return;
    }

    setGuardando(true);
    try {
      await axios.put(`/api/clients/${editando.client.id}`, {
        cedula: form.cedula,
        name: form.name,
        phone: form.phone,
        email: form.email,
        address: form.address,
        owner: form.owner
      });

      // Se envían solo los préstamos que de verdad cambiaron: si alguien entró
      // a corregir el teléfono, no tiene sentido reescribir sus tres préstamos.
      for (const prestamo of editando.prestamos) {
        const b = borradores[prestamo.id];
        if (!b) continue;

        const original = camposDelPrestamo(prestamo);
        const cambio = Object.keys(original).some(
          k => String(original[k] ?? '') !== String(b[k] ?? '')
        );
        if (!cambio) continue;

        await axios.put(`/api/loans/${prestamo.id}`, {
          amount: parseFloat(b.amount),
          interest_rate: parseFloat(b.interest_rate),
          frequency: b.frequency,
          start_date: b.start_date || null,
          maturity_date: b.maturity_date
            ? new Date(`${b.maturity_date}T00:00:00`).toISOString()
            : null,
          fiador_nombre: b.fiador_nombre || null,
          fiador_cedula: b.fiador_cedula || null,
          fiador_telefono: b.fiador_telefono || null,
          fiador_direccion: b.fiador_direccion || null,
          fiador_email: b.fiador_email || null
        });
      }

      // El préstamo nuevo, si se llenó. Va al final para que un fallo aquí no
      // deje a medias las correcciones de arriba.
      const nuevo = borradores[NUEVO];
      const creaPrestamo = nuevo && parseFloat(nuevo.amount) > 0;
      if (creaPrestamo) {
        await axios.post('/api/loans', {
          client_id: editando.client.id,
          amount: parseFloat(nuevo.amount),
          interest_rate: parseFloat(nuevo.interest_rate),
          frequency: nuevo.frequency,
          start_date: nuevo.start_date,
          maturity_date: nuevo.maturity_date
            ? new Date(`${nuevo.maturity_date}T00:00:00`).toISOString()
            : null,
          fiador_nombre: nuevo.fiador_nombre || null,
          fiador_cedula: nuevo.fiador_cedula || null,
          fiador_telefono: nuevo.fiador_telefono || null,
          fiador_direccion: nuevo.fiador_direccion || null,
          fiador_email: nuevo.fiador_email || null
        });
      }

      setEditando(null);
      onRefresh();
      alert(creaPrestamo
        ? `✅ Préstamo de ${moneda(parseFloat(nuevo.amount))} creado para ${form.name}`
        : '✅ Cambios guardados');
    } catch (err) {
      setErrorEdit('❌ ' + (err.response?.data?.error || err.message));
    }
    setGuardando(false);
  };

  // Todo el que se borra se va a la papelera: nada se pierde en el acto y hay un
  // mes para arrepentirse. Lo que cambia de un caso a otro es lo que se le dice
  // antes, porque no es lo mismo quitar un nombre tecleado mal que sacar de la
  // lista a alguien que lleva años abonando.
  const handleDeleteClient = async (client) => {
    const d = deuda(client);
    const cuantos = d.prestamos === 1 ? '1 préstamo' : `${d.prestamos} préstamos`;
    // Las cifras van en el aviso para que la decisión no dependa de creerse una
    // etiqueta: se ve lo que debe, lo que abonó y lo que falta.
    // El desglose importa: alguien puede llevar el interés al día y deber el
    // capital entero, que es el caso en que más fácil se borra por error.
    const cuentas = d.total > 0
      ? `${cuantos} · debe ${moneda(d.total)}` +
        (d.capital > 0 ? ` (${moneda(d.capital)} de capital` : '') +
        (d.capital > 0 && d.interes > 0 ? ` y ${moneda(d.interes)} de interés)` : d.capital > 0 ? ')' : '') +
        ` · ya abonó ${moneda(d.pagado)}`
      : `${cuantos} · ya abonó ${moneda(d.pagado)}`;

    const papelera = '\n\nSe va a la papelera con todo lo suyo. Ahí lo puedes ' +
                     'recuperar entero durante 30 días; pasado el mes se borra ' +
                     'para siempre.';

    // El caso más común de querer borrar: se creó por error y todavía no tiene
    // nada. No hay cuentas que mostrar ni decisión que tomar.
    if (d.prestamos === 0) {
      if (window.confirm(
        `¿Eliminar a ${client.name}?\n\nNo tiene ningún préstamo registrado.` + papelera
      )) {
        confirmDeleteClient(client);
      }
      return;
    }

    if (d.todoPagado) {
      if (window.confirm(
        `✅ ${client.name} no debe nada.\n${cuentas}\n\n` +
        `¿Eliminarlo o guardarlo como pagado?\n\nAceptar = Eliminar | Cancelar = Guardar`
      )) {
        confirmDeleteClient(client);
      } else {
        confirmArchiveClient(client);
      }
      return;
    }

    // Ya abonó: aquí lo que se está borrando es la prueba de lo que entregó, y
    // eso no lo reconstruye nadie de memoria. Se deja hacer —quien pone la plata
    // decide a quién tiene en su lista— pero diciéndolo con todas las letras y
    // recordando que guardarlo como pagado hace lo mismo sin borrar nada.
    if (d.pagado > 0) {
      if (window.confirm(
        `⚠️ ${client.name} ya abonó ${moneda(d.pagado)} y todavía debe ${moneda(d.total)}.\n` +
        `${cuentas}\n\n` +
        `Se lleva su historial completo: ${cuantos} y todos sus abonos.\n` +
        `Si solo quieres dejar de verlo en la lista, guárdalo como pagado y no ` +
        `se borra nada.` + papelera
      )) {
        confirmDeleteClient(client);
      }
      return;
    }

    // Sin un solo abono: o es un error de tecleo, o es plata prestada hace un
    // rato. Se deja borrar, pero diciendo cuánto se lleva por delante.
    if (window.confirm(
      `⚠️ ${client.name} tiene ${cuantos} por ${moneda(d.total)} y ningún abono.\n\n` +
      `Se lleva también ${d.prestamos === 1 ? 'ese préstamo' : 'esos préstamos'}.` +
      papelera + `\n\n¿Es un error de tecleo? Aceptar = Eliminar | Cancelar = Dejarlo`
    )) {
      confirmDeleteClient(client);
    }
  };

  // Recibe el cliente por parámetro. Antes lo leía del estado, y quien llamaba
  // hacía `setModalClient(...)` justo antes: React no aplica el estado hasta el
  // siguiente render, así que aquí seguía llegando el valor anterior —null— y la
  // función se salía por el `if` de arriba SIN HACER NADA y sin decir nada.
  // Desde la lista, el botón de la papelera no borraba y no avisaba de nada.
  const confirmDeleteClient = async (cliente = modalClient) => {
    if (!cliente) return;
    try {
      const { data } = await axios.delete(`/api/clients/${cliente.id}`);
      setModalOpen(false);
      setModalClient(null);
      onRefresh();
      const r = data || {};
      alert(`🗑️ ${cliente.name} está en la papelera.\n\n` +
            (r.pagos ? `Se guardaron ${r.prestamos} préstamo(s) y ${r.pagos} abono(s).\n` : '') +
            `Tienes ${r.dias || 30} días para recuperarlo desde Papelera.`);
    } catch (err) {
      // El servidor manda el mensaje ya escrito para que lo lea una persona.
      // Mostrar `err.message` en su lugar dejaba «Request failed with status
      // code 409» en pantalla.
      const delServidor = err.response?.data?.error;
      alert(delServidor || ('No se pudo eliminar el cliente: ' + err.message));
    }
  };

  const confirmArchiveClient = async (cliente = modalClient) => {
    if (!cliente) return;
    try {
      await axios.patch(`/api/clients/${cliente.id}/status`, { status: 'paid_off' });
      setModalOpen(false);
      setModalClient(null);
      onRefresh();
      alert('Cliente guardado como pagado');
    } catch (err) {
      alert('Error al guardar cliente: ' + err.message);
    }
  };

  return (
    <>
    <div className="clients-page">
      <h2>👥 Base de Datos de Clientes</h2>

      {/* Formulario */}
      <div className="form-card">
        <h3>➕ Agregar Nuevo Cliente</h3>

        {error && (
          <div style={{
            backgroundColor: '#fee2e2',
            color: '#991b1b',
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '20px',
            fontSize: '14px',
            fontWeight: 'bold'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleAddClient}>
          <input
            type="text"
            placeholder="Cédula *"
            value={cedula}
            onChange={(e) => setCedula(e.target.value)}
            required
          />
          <input
            type="text"
            placeholder="Nombre completo *"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <input
            type="tel"
            placeholder="Teléfono *"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            required
          />
          <input
            type="text"
            placeholder="Dirección"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
          <input
            type="email"
            placeholder="Email (opcional)"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {usaPropietarios && (
            <>
              <input
                type="text"
                list="lista-propietarios"
                placeholder="Propietario (ej: carlos, socio)"
                value={owner}
                onChange={(e) => setOwner(e.target.value)}
              />
              <datalist id="lista-propietarios">
                {owners.filter(o => o !== SIN_ASIGNAR).map(o => (
                  <option key={o} value={o} />
                ))}
              </datalist>
            </>
          )}
          <button type="submit" className="btn-primary">✅ Guardar Cliente</button>
        </form>
      </div>

      {/* Tabla de clientes */}
      <div className="table-container">
        <h3>
          Clientes Registrados ({visibleClients.length}
          {(termino !== '' || (usaPropietarios && ownerFilter !== 'todos')) && ` de ${clients.length}`})
        </h3>

        <div style={{ position: 'relative', marginBottom: '15px' }}>
          <input
            type="text"
            value={busquedaCliente}
            onChange={(e) => setBusquedaCliente(e.target.value)}
            placeholder="🔍 Buscar por nombre, cédula o teléfono"
            style={{
              width: '100%', padding: '11px 38px 11px 14px', fontSize: '15px',
              borderRadius: '8px', border: '1px solid #d1d5db', boxSizing: 'border-box'
            }}
          />
          {busquedaCliente !== '' && (
            <button
              type="button"
              onClick={() => setBusquedaCliente('')}
              title="Ver todos"
              style={{
                position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '18px', color: '#9ca3af', lineHeight: 1, padding: '0 6px'
              }}
            >
              ×
            </button>
          )}
        </div>

        {termino !== '' && visibleClients.length === 0 && (
          <p style={{ color: '#6b7280', fontSize: '14px', padding: '10px 0' }}>
            Ningún cliente coincide con «{busquedaCliente}».
            {usaPropietarios && ownerFilter !== 'todos' &&
              ` Estás viendo solo los de ${ownerFilter}.`}
          </p>
        )}

        {/* Separación por propietario */}
        {usaPropietarios && (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '15px' }}>
          {['todos', ...owners].map(o => {
            const activo = ownerFilter === o;
            const total = o === 'todos' ? clients.length : contarPor(o);
            return (
              <button
                key={o}
                onClick={() => setOwnerFilter(o)}
                style={{
                  padding: '8px 14px',
                  borderRadius: '6px',
                  border: activo ? 'none' : '1px solid #e5e7eb',
                  background: activo ? '#667eea' : 'white',
                  color: activo ? 'white' : '#374151',
                  cursor: 'pointer',
                  fontWeight: activo ? 'bold' : 'normal',
                  fontSize: '14px',
                  textTransform: 'capitalize'
                }}
              >
                {o} ({total})
              </button>
            );
          })}
        </div>
        )}

        {visibleClients.length === 0 ? (
          <p className="empty-state">No hay clientes registrados aún</p>
        ) : (
          <table className="clients-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Cédula</th>
                <th>Nombre</th>
                {usaPropietarios && <th>👤 Propietario</th>}
                <th>⭐ Score</th>
                <th>👁️ Perfil Público</th>
                <th>📅 Fecha Préstamo</th>
                <th>💵 Monto</th>
                <th>📊 Interés %</th>
                <th>⏰ Vencimiento</th>
                <th>Teléfono</th>
                <th>⚙️</th>
              </tr>
            </thead>
            <tbody>
              {visibleClients.map((client, idx) => {
                // Con varios préstamos abiertos la fila mostraba solo uno, sin
                // avisar: se veían 800.000 cuando el cliente debía 1.300.000.
                // Ahora el monto es la suma, el interés dice «varios» si no
                // coinciden y el vencimiento es el más próximo, que es el que
                // reclama atención.
                const loans = loansData[client.id] || [];
                const varios = loans.length > 1;
                const loan = loans.length > 0 ? loans[0] : null;

                const loanDate = loan ? formatearFecha(loan.start_date) : '-';
                const loanAmount = loan
                  ? moneda(loans.reduce((s, l) => s + l.amount, 0))
                  : '-';

                const tasas = [...new Set(loans.map(l => l.interest_rate))];
                const interestRate = !loan
                  ? '-'
                  : tasas.length === 1 ? `${tasas[0]}%` : 'varios';

                // El que vence antes manda en el semáforo
                const loanVence = loans
                  .filter(l => l.maturity_date)
                  .sort((a, b) => new Date(a.maturity_date) - new Date(b.maturity_date))[0] || loan;

                const score = scoresData[client.id] || {};
                const stars = Math.round((score.credit_score || 0) / 20);
                const starDisplay = '⭐'.repeat(Math.max(1, stars)) || '◯';
                const scoreColor = score.credit_score >= 80 ? '#10b981' : score.credit_score >= 60 ? '#f59e0b' : score.credit_score >= 40 ? '#f97316' : '#ef4444';

                const isPublic = publicProfiles[client.id];

                return (
                  <tr key={client.id}>
                    <td>{idx + 1}</td>
                    <td><strong>{client.cedula || '-'}</strong></td>
                    <td>
                      {client.name}
                      {/* La dirección va bajo el nombre y no en columna propia:
                          la tabla ya tiene doce y se salía de la pantalla. */}
                      {client.address && (
                        <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                          📍 {client.address}
                        </div>
                      )}
                    </td>
                    {usaPropietarios && (
                    <td>
                      <span style={{
                        padding: '3px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        background: client.owner ? '#eef2ff' : '#f3f4f6',
                        color: client.owner ? '#4338ca' : '#9ca3af',
                        textTransform: 'capitalize'
                      }}>
                        {client.owner || SIN_ASIGNAR}
                      </span>
                    </td>
                    )}
                    <td><strong style={{ color: scoreColor, fontSize: '14px' }}>
                      {starDisplay} ({score.credit_score || 0})
                    </strong></td>
                    <td>
                      <button
                        onClick={() => togglePublicProfile(client.id, isPublic)}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '4px',
                          border: 'none',
                          cursor: 'pointer',
                          background: isPublic ? '#10b981' : '#e5e7eb',
                          color: isPublic ? 'white' : '#6b7280',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}
                      >
                        {isPublic ? '✓ Público' : '○ Privado'}
                      </button>
                    </td>
                    <td>{loanDate}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <strong>{loanAmount}</strong>
                      {varios && (
                        <div style={{
                          display: 'inline-block', marginLeft: '6px', padding: '1px 7px',
                          borderRadius: '10px', background: '#fef3c7', color: '#92400e',
                          fontSize: '11px', fontWeight: 'bold', verticalAlign: 'middle'
                        }}>
                          {loans.length} préstamos
                        </div>
                      )}
                    </td>
                    <td><strong style={{ color: '#10b981' }}>{interestRate}</strong></td>
                    <td>
                      {loanVence ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ fontSize: '16px' }}>
                            {getMaturityStatus(loanVence).icon}
                          </span>
                          <span style={{ color: getMaturityStatus(loanVence).color, fontSize: '12px', fontWeight: 'bold' }}>
                            {getMaturityStatus(loanVence).status}
                          </span>
                        </div>
                      ) : '-'}
                    </td>
                    <td>{client.phone || '-'}</td>
                    <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      {/* Prestarle otra vez. Estaba solo dentro del lápiz, y
                          ahí no se encuentra: quien va a prestar viene mirando
                          la fila del cliente, no buscando una pestaña. */}
                      <button
                        onClick={() => abrirEdicion(client, true)}
                        title={`Prestarle otra vez a ${client.name}`}
                        style={{
                          backgroundColor: '#10b981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '6px 10px',
                          cursor: 'pointer',
                          fontSize: '16px',
                          lineHeight: '1',
                          marginRight: '6px'
                        }}
                      >
                        ➕
                      </button>
                      <button
                        onClick={() => abrirEdicion(client)}
                        title="Editar cliente y préstamo"
                        style={{
                          backgroundColor: '#667eea',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '6px 10px',
                          cursor: 'pointer',
                          fontSize: '16px',
                          lineHeight: '1',
                          marginRight: '6px'
                        }}
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDeleteClient(client)}
                        title="Eliminar cliente"
                        style={{
                          backgroundColor: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '6px 10px',
                          cursor: 'pointer',
                          fontSize: '16px',
                          lineHeight: '1'
                        }}
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>

    {/* Ficha de edición */}
    {editando && (
      <div
        onClick={() => !guardando && setEditando(null)}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          padding: '30px 15px', zIndex: 100, overflowY: 'auto'
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: 'white', borderRadius: '12px', padding: '25px',
            width: '100%', maxWidth: '520px', boxShadow: '0 10px 40px rgba(0,0,0,.25)'
          }}
        >
          <h3 style={{ marginTop: 0 }}>
            {prestamoActivo === NUEVO && editando.prestamos.length > 0
              ? `➕ Prestarle otra vez a ${editando.client.name}`
              : `✏️ Editar ${editando.client.name}`}
          </h3>

          {errorEdit && (
            <div style={{
              background: '#fee2e2', color: '#991b1b', padding: '12px',
              borderRadius: '6px', marginBottom: '15px', fontSize: '14px', fontWeight: 'bold'
            }}>
              {errorEdit}
            </div>
          )}

          <form onSubmit={guardarEdicion}>
            <div style={{ fontWeight: 'bold', color: '#6b7280', fontSize: '13px', margin: '0 0 8px' }}>
              DATOS DEL CLIENTE
            </div>
            <input type="text" placeholder="Cédula *" required {...campo('cedula')} />
            <input type="text" placeholder="Nombre completo *" required {...campo('name')} />
            <input type="tel" placeholder="Teléfono *" required {...campo('phone')} />
            <input type="text" placeholder="Dirección" {...campo('address')} />
            <input type="email" placeholder="Email (opcional)" {...campo('email')} />
            {usaPropietarios && (
              <input type="text" list="lista-propietarios" placeholder="Propietario" {...campo('owner')} />
            )}

            {(() => {
              const enNuevo = prestamoActivo === NUEVO;
              return (
              <>
                <div style={{ fontWeight: 'bold', color: '#6b7280', fontSize: '13px', margin: '20px 0 8px' }}>
                  {editando.prestamos.length === 0
                    ? 'PRÉSTAMO NUEVO'
                    : editando.prestamos.length === 1
                      ? 'PRÉSTAMOS'
                      : `PRÉSTAMOS (${editando.prestamos.length})`}
                </div>

                {/* Se elige cuál corregir, o se añade otro. Lo escrito en cada
                    pestaña se conserva al cambiar, y al guardar se envían todos
                    los que hayan cambiado más el nuevo si tiene monto. */}
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
                  {editando.prestamos.map((p, i) => {
                    const activo = p.id === prestamoActivo;
                    const b = borradores[p.id] || {};
                    const tocado = Object.keys(camposDelPrestamo(p)).some(
                      k => String(camposDelPrestamo(p)[k] ?? '') !== String(b[k] ?? '')
                    );
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setPrestamoActivo(p.id)}
                        style={{
                          padding: '7px 12px',
                          borderRadius: '6px',
                          border: activo ? 'none' : '1px solid #e5e7eb',
                          background: activo ? '#667eea' : 'white',
                          color: activo ? 'white' : '#374151',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: activo ? 'bold' : 'normal'
                        }}
                      >
                        {i === 0 && editando.prestamos.length > 1
                          ? 'Más reciente'
                          : formatearFecha(p.start_date)}
                        {' · '}{moneda(p.amount)}
                        {tocado && ' •'}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => setPrestamoActivo(NUEVO)}
                    style={{
                      padding: '7px 12px',
                      borderRadius: '6px',
                      border: enNuevo ? 'none' : '1px dashed #10b981',
                      background: enNuevo ? '#10b981' : '#f0fdf4',
                      color: enNuevo ? 'white' : '#047857',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: 'bold'
                    }}
                  >
                    ➕ Otro préstamo
                    {!enNuevo && parseFloat(borradores[NUEVO]?.amount) > 0 && ' •'}
                  </button>
                </div>

                {enNuevo && (
                  <div style={{
                    padding: '10px 12px', borderRadius: '6px', marginBottom: '12px',
                    background: '#f0fdf4', border: '1px solid #bbf7d0',
                    color: '#166534', fontSize: '13px'
                  }}>
                    Préstamo nuevo para <strong>{form.name || editando.client.name}</strong>.
                    Los anteriores siguen como están: cada uno lleva su propia cuenta de
                    interés. Si dejas el monto vacío no se crea nada.
                  </div>
                )}

                <label style={{ fontSize: '13px', color: '#6b7280' }}>Monto ($)</label>
                <input
                  type="number"
                  step="0.01"
                  required={!enNuevo}
                  placeholder={enNuevo ? 'Cuánto le prestas ahora' : undefined}
                  {...campoPrestamo('amount')}
                />

                <label style={{ fontSize: '13px', color: '#6b7280' }}>Interés (%)</label>
                <input type="number" step="0.1" required {...campoPrestamo('interest_rate')} />

                <label style={{ fontSize: '13px', color: '#6b7280' }}>Frecuencia</label>
                <select {...campoPrestamo('frequency')}>
                  <option value="daily">Diario</option>
                  <option value="weekly">Semanal</option>
                  <option value="biweekly">Quincenal</option>
                  <option value="monthly">Mensual</option>
                </select>

                <label style={{ fontSize: '13px', color: '#6b7280' }}>Fecha de inicio</label>
                <input type="date" {...campoPrestamo('start_date')} />

                <label style={{ fontSize: '13px', color: '#6b7280' }}>Fecha de vencimiento</label>
                <input type="date" {...campoPrestamo('maturity_date')} />
                {!borradores[prestamoActivo]?.maturity_date && (
                  <small style={{ color: '#92400e', display: 'block', marginTop: '4px' }}>
                    Sin fecha de vencimiento este préstamo no aparece en Vencimientos
                    ni en Estadísticas.
                  </small>
                )}

                <div style={{ fontWeight: 'bold', color: '#6b7280', fontSize: '13px', margin: '20px 0 8px' }}>
                  FIADOR <span style={{ fontWeight: 'normal' }}>(opcional)</span>
                </div>
                {[
                  ['fiador_nombre', 'Nombre completo'],
                  ['fiador_cedula', 'Cédula'],
                  ['fiador_telefono', 'Teléfono'],
                  ['fiador_direccion', 'Dirección'],
                  ['fiador_email', 'Correo']
                ].map(([campo, etiqueta]) => (
                  <input key={campo} type="text" placeholder={etiqueta} {...campoPrestamo(campo)} />
                ))}
              </>
              );
            })()}

            <div style={{ display: 'flex', gap: '10px', marginTop: '25px' }}>
              <button
                type="button"
                onClick={() => setEditando(null)}
                disabled={guardando}
                style={{
                  flex: 1, padding: '12px', borderRadius: '6px', cursor: 'pointer',
                  border: '1px solid #e5e7eb', background: 'white', color: '#374151'
                }}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={guardando}
                style={{
                  flex: 1, padding: '12px', borderRadius: '6px', border: 'none',
                  background: '#667eea', color: 'white', fontWeight: 'bold',
                  cursor: guardando ? 'not-allowed' : 'pointer', opacity: guardando ? 0.7 : 1
                }}
              >
                {guardando
                  ? 'Guardando...'
                  : parseFloat(borradores[NUEVO]?.amount) > 0
                    ? '💾 Guardar y crear el préstamo'
                    : '💾 Guardar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    )}
    </>
  );
}

export default ClientList;
